import { DashboardCard } from '@/common/dashboard/DashboardCard';
import { DimTree } from '@/common/entityFramework/entityPerspective/support/DimTree';
import { MainBox } from '@/common/layout/MainBox';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { TreeUtils } from '@/common/utils/TreeUtils';
import { far, fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box, MenuItem, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { DemReconAIInsights } from './charts/DemReconAIInsights';
import { DemReconChannelDiscrepanciesChart } from './charts/DemReconChannelDiscrepanciesChart';
import { DemReconDiscrepancyDriversChart } from './charts/DemReconDiscrepancyDriversChart';
import { DemReconTrendChart } from './charts/DemReconTrendChart';
import { DiscrepancyHighlightCards } from './DiscrepancyDollarsCards';

export const DemReconDashboard = () => {
  const periodicityToPeriodCodes = useRef();

  const [periods, setPeriods] = useState([]); // safe default so .map doesn't explode
  const [prodTree, setProdTree] = useState();

  const [startPeriod, setStartPeriod] = useState();
  const [endPeriod, setEndPeriod] = useState();
  const [periodicity, setPeriodicity] = useState('QTR');
  const [prodIds, setProdIds] = useState();

  const rsrc = useRsrc();

  useEffect(() => {
    rsrc.data('gtn', 'RECON_DASHBOARD_FILTERS_DATA_PLUGIN').then((res) => {
      periodicityToPeriodCodes.current = res.periodicityToPeriodCodes;

      // initialize periods/start/end using current periodicity
      handlePeriodicityChange(periodicity);

      setProdTree(res.prodTree);
      const processed = TreeUtils.parseTree(res.prodTree).leafIds;
      setProdIds(processed);
    });
  }, []);

  const handlePeriodicityChange = (newPeriodicity) => {
    setPeriodicity(newPeriodicity);

    const nextPeriods = periodicityToPeriodCodes.current?.[newPeriodicity] ?? [];
    setPeriods(nextPeriods);

    const nextStart = nextPeriods[0];
    const nextEnd = nextPeriods[nextPeriods.length - 1];
    setStartPeriod(nextStart);
    setEndPeriod(nextEnd);
  };

  if (!prodTree) return null;

  return (
    <MainBox>
      {/* Title */}
      <Box sx={{ mb: 1 }}>
        <Title icon={fas.faChartLine} title="Reconciliation Dashboard" />
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          select
          variant="outlined"
          label="Start Period"
          value={startPeriod ?? ''}
          onChange={(event) => setStartPeriod(event.target.value)}
        >
          {(periods ?? []).map((periodValue) => (
            <MenuItem key={periodValue} value={periodValue}>
              {periodValue}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          select
          variant="outlined"
          label="End Period"
          value={endPeriod ?? ''}
          onChange={(event) => setEndPeriod(event.target.value)}
        >
          {(periods ?? []).map((periodValue) => (
            <MenuItem key={periodValue} value={periodValue}>
              {periodValue}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          select
          variant="outlined"
          label="Periodicity"
          value={periodicity}
          onChange={(event) => handlePeriodicityChange(event.target.value)}
        >
          <MenuItem value="MON">Month</MenuItem>
          <MenuItem value="QTR">Quarter</MenuItem>
          <MenuItem value="YEAR">Year</MenuItem>
        </TextField>

        <DimTree
          displayName="Product"
          treeData={prodTree}
          selectedIds={prodIds}
          onFilterChange={(ids) => setProdIds(ids)}
        />
      </Box>

      <SpacerLayout>
        <Spacer vertical>
          <Spacer weights={[3, 2]}>
            <DashboardCard title="AI Insights" icon={far.faSparkles} variant="bold">
              <DemReconAIInsights />
            </DashboardCard>

            <DiscrepancyHighlightCards prodIds={prodIds} />
          </Spacer>

          <Spacer>
            <DashboardCard title="Discrepancy Dollars by Channel">
              <DemReconChannelDiscrepanciesChart prodIds={prodIds} />
            </DashboardCard>

            <DashboardCard title="Discrepancy Drivers">
              <DemReconDiscrepancyDriversChart prodIds={prodIds} />
            </DashboardCard>
          </Spacer>

          <DashboardCard title="Discrepancy Trend">
            <DemReconTrendChart startPeriod={startPeriod} endPeriod={endPeriod} periodicity={periodicity} prodIds={prodIds} />
          </DashboardCard>
        </Spacer>
      </SpacerLayout>
    </MainBox>
  );
};